export enum CoordinateKind {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Latitude = 'latitude',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Longitude = 'longitude',
}

//#region Cardinal Direction
export type LongitudeDirection = 'E' | 'W';
export type LatitudeDirection = 'N' | 'S';
export type CardinalDirection = LatitudeDirection | LongitudeDirection;

export function isLatitudeDirection(value: unknown): value is LatitudeDirection {
  return value === 'N' || value === 'S';
}
export function isLongitudeDirection(value: unknown): value is LongitudeDirection {
  return value === 'E' || value === 'W';
}

export function isCardinalDirection(value: unknown): value is CardinalDirection {
  return isLatitudeDirection(value) || isLongitudeDirection(value);
}

export function assertCardinalDirection(value: unknown): asserts value is CardinalDirection {
  if (!isCardinalDirection(value)) {
    throw new Error(
      `"${value}" is not one of the valid cardinal direction of "N", "E", "W", or "S".`,
    );
  }
}
//#endregion

//#region utility types
export type LatitudeLongitude<TLat, TLng = TLat> = {
  latitude: TLat;
  longitude: TLng;
};
//#endregion

//#region Value Types
export interface DegreesDecimalMinutes {
  degrees: number;
  decimalMinutes: number;
}
const isDegreesDecimalMinutes = (value: CoordinateValue): value is DegreesDecimalMinutes =>
  'decimalMinutes' in value;

function getDegreesDecimalMinutes(value: CoordinateValue): DegreesDecimalMinutes {
  return isDecimalDegrees(value)
    ? decimalDegreesToDegreesDecimalMinutes(value)
    : isDegreesDecimalMinutes(value)
    ? value
    : degreesMinutesSecondsToDegreesDecimalMinutes(value);
}
export interface DegreesMinutesSeconds {
  degrees: number;
  minutes: number;
  seconds: number;
}
const isDegreesMinutesSeconds = (value: CoordinateValue): value is DegreesMinutesSeconds =>
  'seconds' in value;

function getDegreesMinutesSeconds(value: CoordinateValue): DegreesMinutesSeconds {
  return isDecimalDegrees(value)
    ? decimalDegreesToDegreesMinutesSeconds(value)
    : isDegreesDecimalMinutes(value)
    ? degreesDecimalMinutesToDegreesMinutesSeconds(value)
    : value;
}

export type DecimalDegrees = {
  decimalDegrees: number;
};
const isDecimalDegrees = (value: CoordinateValue): value is DecimalDegrees =>
  'decimalDegrees' in value;

function getDecimalDegrees(value: CoordinateValue): DecimalDegrees {
  return isDegreesDecimalMinutes(value)
    ? degreesDecimalMinutesToDecimalDegrees(value)
    : isDegreesMinutesSeconds(value)
    ? degreesMinutesSecondsToDecimalDegrees(value)
    : value;
}

export type CoordinateValue = DecimalDegrees | DegreesDecimalMinutes | DegreesMinutesSeconds;
//#endregion

export class CoordinatePair {
  constructor(latitude: LatitudeValue, longitude: LongitudeValue) {
    this.latitude = new Latitude(latitude);
    this.longitude = new Longitude(longitude);
  }

  public readonly latitude: Latitude;
  public readonly longitude: Longitude;

  public static create(
    value:
      | [LatitudeValue, LongitudeValue]
      | { lat: LatitudeValue; lng: LongitudeValue }
      | { latitude: LatitudeValue; longitude: LongitudeValue },
  ) {
    if (value instanceof Array) {
      return CoordinatePair.fromArray(value);
    } else if ('lat' in value) {
      const { lat, lng } = value;
      return new CoordinatePair(lat, lng);
    } else {
      return new CoordinatePair(value.latitude, value.longitude);
    }
  }

  public static fromArray([lat, lng]: [LatitudeValue, LongitudeValue]) {
    return new CoordinatePair(lat, lng);
  }

  public toArray(): [Latitude, Longitude] {
    return [this.latitude, this.longitude];
  }

  public getDegreesDecimalMinutes(
    format: DegreesDecimalMinutesFormatString = 'DDD° MM.MMM',
  ): string {
    return this._printCsv((c) => c.getDegreesDecimalMinutes(format));
  }

  public getDecimalDegrees(format: DecimalDegreesFormatString = '±DDD.DDDDD'): string {
    return this._printCsv((c) => c.getDecimalDegrees(format));
  }

  public getDegreesMinutesSeconds(
    format: DegreesMinutesSecondsFormatString = `DDD° MM' SS.S"`,
  ): string {
    return this._printCsv((c) => c.getDegreesMinutesSeconds(format));
  }

  private _printCsv(printFn: (c: Coordinate) => string) {
    return this.toArray()
      .map((c) => printFn(c))
      .join(', ');
  }

  public toString(format: CoodrindateFormatString = '±DDD.DDDDD'): string {
    switch (format) {
      case '±DDD.DDDDD':
      case 'DDD.DDDDD°':
        return this.getDecimalDegrees(format);
      case `DDD° MM' SS.S"`:
      case 'DDD° MM:SS.S':
        return this.getDegreesMinutesSeconds(format);
      case 'DDD° MM.MMM':
        return this.getDegreesDecimalMinutes(format);
    }
  }
}

//#region Coordinate
type DecimalDegreesFormatString = 'DDD.DDDDD°' | '±DDD.DDDDD';
type DegreesMinutesSecondsFormatString = `DDD° MM' SS.S"` | 'DDD° MM:SS.S';
type DegreesDecimalMinutesFormatString = `DDD° MM.MMM`;
type CoodrindateFormatString =
  | DecimalDegreesFormatString
  | DegreesMinutesSecondsFormatString
  | DegreesDecimalMinutesFormatString;

export abstract class Coordinate {
  public readonly direction: CardinalDirection;
  public readonly kind: CoordinateKind;
  private get _degreesDecimalMinutes() {
    return (this._degreesDecimalMinutesValue =
      this._degreesDecimalMinutesValue ?? getDegreesDecimalMinutes(this._inputValue));
  }

  get decimalMinutes() {
    return this._degreesDecimalMinutes.decimalMinutes;
  }

  get degrees() {
    return this.degreesMinutesSeconds.degrees;
  }

  get minutes() {
    return this.degreesMinutesSeconds.minutes;
  }

  get seconds() {
    return this.degreesMinutesSeconds.seconds;
  }

  private get degreesMinutesSeconds(): DegreesMinutesSeconds {
    return (this._degreesMinutesSeconds =
      this._degreesMinutesSeconds ?? getDegreesMinutesSeconds(this._inputValue));
  }

  public get decimalDegrees(): number {
    return this._decimalDegreesLazy.decimalDegrees;
  }

  public get value(): number {
    if (this._value == null) {
      this._value =
        this.direction === 'W' || this.direction === 'S'
          ? -1 * this.decimalDegrees
          : this.decimalDegrees;
    }
    return this._value;
  }

  public get decimalDegreesUnsigned(): number {
    return this._decimalDegreesLazy.decimalDegrees;
  }
  private get _decimalDegreesLazy(): DecimalDegrees {
    if (this._decimalDegrees == null) {
      this._decimalDegrees = getDecimalDegrees(this._inputValue);
    }
    return this._decimalDegrees;
  }

  constructor(value: CoordinateValue, direction: CardinalDirection, kind: CoordinateKind) {
    this._inputValue = value;
    this.kind = kind;
    this.direction = direction;
  }

  private readonly _inputValue: CoordinateValue;
  private _value?: number;
  private _degreesDecimalMinutesValue?: DegreesDecimalMinutes;
  private _degreesMinutesSeconds?: DegreesMinutesSeconds;
  private _decimalDegrees?: DecimalDegrees;

  public static create(value: CoordinateValue & { direction: CardinalDirection }): Coordinate {
    if (isLatitudeDirection(value.direction)) {
      return new Latitude({
        ...value,
        direction: value.direction,
      });
    } else {
      return new Longitude({
        ...value,
        direction: value.direction,
      });
    }
  }

  public static parse(value: string): Coordinate {
    throw new Error('Coordinate.parse: not implemented');
  }

  public getDegreesDecimalMinutes(
    format: DegreesDecimalMinutesFormatString = 'DDD° MM.MMM',
  ): string {
    throw new Error('Coordinate: printDecimalDegrees not implemented');
  }
  public getDecimalDegrees(format: DecimalDegreesFormatString = '±DDD.DDDDD'): string {
    throw new Error('Coordinate: printDecimalDegrees not implemented');
  }

  public getDegreesMinutesSeconds(
    format: DegreesMinutesSecondsFormatString = `DDD° MM' SS.S"`,
  ): string {
    throw new Error('Coordinate: printDecimalDegrees not implemented');
  }

  public toString(format: CoodrindateFormatString = '±DDD.DDDDD'): string {
    switch (format) {
      case '±DDD.DDDDD':
      case 'DDD.DDDDD°':
        return this.getDecimalDegrees(format);
      case `DDD° MM' SS.S"`:
      case 'DDD° MM:SS.S':
        return this.getDegreesMinutesSeconds(format);
      case 'DDD° MM.MMM':
        return this.getDegreesDecimalMinutes(format);
    }
  }
}
function coerceCoordinateValue(value: LatitudeValue | LongitudeValue): CoordinateValue {
  return typeof value === 'number' ? { decimalDegrees: Math.abs(value) } : value;
}
//#endregion

//#region Latitude
export type LatitudeValue = (CoordinateValue & { direction: LatitudeDirection }) | number;

function getLatitudeDirection(value: LatitudeValue): LatitudeDirection {
  if (typeof value === 'number') {
    return value >= 0 ? 'N' : 'S';
  }
  return value.direction;
}
export class Latitude extends Coordinate {
  public readonly direction: LatitudeDirection;
  public readonly kind: CoordinateKind.Latitude;

  constructor(value: LatitudeValue) {
    super(coerceCoordinateValue(value), getLatitudeDirection(value), CoordinateKind.Latitude);
  }
}
//#endregion

//#region Longitude
export type LongitudeValue = (CoordinateValue & { direction: LongitudeDirection }) | number;
function getLongitudeDirection(value: LongitudeValue): LongitudeDirection {
  if (typeof value === 'number') {
    return value >= 0 ? 'E' : 'W';
  }
  return value.direction;
}
export class Longitude extends Coordinate {
  public readonly direction: LongitudeDirection;
  public readonly kind: CoordinateKind.Longitude;

  constructor(value: LongitudeValue) {
    super(coerceCoordinateValue(value), getLongitudeDirection(value), CoordinateKind.Longitude);
  }
}

//#endregion

//#region Conversion functions
export function decimalDegreesToDegreesMinutesSeconds({
  decimalDegrees,
}: DecimalDegrees): DegreesMinutesSeconds {
  decimalDegrees = Math.abs(decimalDegrees);
  const degrees = Math.trunc(decimalDegrees);
  const minutes = Math.trunc(60 * (decimalDegrees - degrees));
  const seconds = 3600 * (decimalDegrees - degrees) - 60 * minutes;
  return { degrees, minutes, seconds };
}

export function decimalDegreesToDegreesDecimalMinutes({
  decimalDegrees,
}: DecimalDegrees): DegreesDecimalMinutes {
  decimalDegrees = Math.abs(decimalDegrees);
  const degrees = Math.trunc(decimalDegrees);
  const decimalMinutes = 60 * (decimalDegrees - degrees);
  return { degrees, decimalMinutes };
}

export function degreesMinutesSecondsToDegreesDecimalMinutes({
  degrees,
  minutes,
  seconds,
}: DegreesMinutesSeconds): DegreesDecimalMinutes {
  const decimalMinutes = minutes + seconds / 60;
  return { degrees, decimalMinutes };
}

export function degreesDecimalMinutesToDecimalDegrees({
  degrees,
  decimalMinutes,
}: DegreesDecimalMinutes): DecimalDegrees {
  const decimalDegrees = degrees + decimalMinutes / 60;
  return { decimalDegrees };
}

export function degreesMinutesSecondsToDecimalDegrees({
  degrees,
  minutes,
  seconds,
}: DegreesMinutesSeconds): DecimalDegrees {
  const decimalDegrees = degrees + minutes / 60 + seconds / 3600;
  return { decimalDegrees };
}

export function degreesDecimalMinutesToDegreesMinutesSeconds({
  degrees,
  decimalMinutes,
}: DegreesDecimalMinutes): DegreesMinutesSeconds {
  const minutes = Math.trunc(decimalMinutes);
  const seconds = (decimalMinutes - minutes) * 60;
  return { degrees, minutes, seconds };
}

//#endregion
